import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, Divider, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, IconButton, Tooltip, Alert,
  CircularProgress, LinearProgress, Menu, MenuItem, Collapse,
} from '@mui/material';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CommentIcon from '@mui/icons-material/Comment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { tasksAPI } from '../../services/api';
import { STATUS_LABELS, STATUS_COLORS } from '../../pages/technician/taskConstants';
import TaskEditorDialog from './TaskEditorDialog';

/**
 * Tareas de campo de una orden de fabricacion, dentro de la vista del gestor.
 *
 * Hay dos formas de crearlas, y conviven: "Generar tareas" vuelca el paquete
 * estandar desde las plantillas, y "Nueva tarea" abre el editor para montar una
 * a mano. Cualquiera de las dos se puede editar despues, con su checklist y sus
 * comentarios.
 */
export default function OrderTasks({ order, onChanged }) {
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [assignMenu, setAssignMenu] = useState(null);   // { anchor, taskId }
  const [technicians, setTechnicians] = useState([]);
  const [editor, setEditor] = useState(null);           // { taskId } | null

  const load = useCallback(async () => {
    try {
      const res = await tasksAPI.list({ manufacturing_order_id: order.id });
      setTasks(res.data);
      setError('');
    } catch (e) {
      setError('No se han podido cargar las tareas.');
    }
  }, [order.id]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await tasksAPI.generateFromOrder(order.id);
      await load();
      setExpanded(true);
      if (res.data.created === 0) {
        setError('No habia tareas nuevas que generar: ya estaban todas creadas.');
      } else {
        setError('');
      }
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se han podido generar las tareas.');
    } finally {
      setBusy(false);
    }
  };

  const openAssign = async (e, taskId) => {
    setAssignMenu({ anchor: e.currentTarget, taskId });
    try {
      const res = await tasksAPI.assignableTechnicians(order.id);
      const team = res.data.team || [];
      const rest = (res.data.all || []).filter((t) => !t.in_team);
      setTechnicians([...team, ...rest]);
    } catch (_) { /* el menu queda vacio */ }
  };

  const assignTo = async (technicianId) => {
    const { taskId } = assignMenu;
    setAssignMenu(null);
    try {
      await tasksAPI.assign(taskId, { technician_id: technicianId });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido asignar.');
    }
  };

  const removeTask = async (taskId) => {
    try {
      await tasksAPI.delete(taskId);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido eliminar.');
    }
  };

  if (tasks === null) {
    return <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>;
  }

  const done = tasks.filter((t) => ['done', 'verified'].includes(t.status)).length;
  const estimated = tasks.reduce((s, t) => s + (t.estimated_hours || 0), 0);
  const actual = tasks.reduce((s, t) => s + (t.actual_hours || 0), 0);
  const pct = tasks.length ? Math.round((done * 100) / tasks.length) : 0;

  return (
    <Box>
      <Divider />
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
                 flexWrap: 'wrap', backgroundColor: '#f5f5f5' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <Typography variant="subtitle2" fontWeight="bold">
            Tareas de campo ({tasks.length})
          </Typography>
        </Box>

        {tasks.length > 0 && (
          <>
            <Box sx={{ minWidth: 140 }}>
              <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1 }} />
              <Typography variant="caption" color="textSecondary">
                {done}/{tasks.length} terminadas
              </Typography>
            </Box>
            <Chip size="small" variant="outlined"
              label={`Previsto ${estimated.toFixed(1)} h · Real ${actual.toFixed(1)} h`}
              color={actual > estimated && estimated > 0 ? 'warning' : 'default'} />
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Crea el paquete estandar de tareas a partir de las plantillas. No duplica las que ya existen.">
          <span>
            <Button size="small" variant="outlined" startIcon={<AutoAwesomeMotionIcon />}
              onClick={generate} disabled={busy || order.status === 'closed'}>
              {busy ? 'Generando...' : 'Generar tareas'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Crear una tarea a mano, con sus indicaciones y su checklist.">
          <span>
            <Button size="small" variant="contained" startIcon={<AddIcon />}
              onClick={() => setEditor({ taskId: null })}
              disabled={busy || order.status === 'closed'}>
              Nueva tarea
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="info" sx={{ mx: 2, my: 1 }} onClose={() => setError('')}>{error}</Alert>}

      <Collapse in={expanded}>
        {tasks.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#eeeeee' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Codigo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tarea</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Asignada a</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fechas</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Prev.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Real</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                  <TableCell align="right" sx={{ width: 130 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((t) => (
                  <TableRow key={t.id} hover
                    onClick={() => setEditor({ taskId: t.id })}
                    sx={{ cursor: 'pointer',
                          ...(t.is_overdue ? { backgroundColor: '#ffebee' } : {}) }}>
                    <TableCell>{t.code}</TableCell>
                    <TableCell>
                      {t.title}
                      {t.checklist_total > 0 && (
                        <Tooltip title={`Checklist: ${t.checklist_done}/${t.checklist_total}`}>
                          <ChecklistIcon fontSize="inherit"
                            sx={{ ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
                        </Tooltip>
                      )}
                      {t.updates_count > 0 && (
                        <Tooltip title={`${t.updates_count} comentario(s) y partes`}>
                          <CommentIcon fontSize="inherit"
                            sx={{ ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {t.assignee?.name || <Typography variant="body2" color="error">Sin asignar</Typography>}
                      {t.created_in_field && (
                        <Chip size="small" label="campo" sx={{ ml: 0.5 }} variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {t.planned_start || '—'}{t.planned_end ? ` / ${t.planned_end}` : ''}
                    </TableCell>
                    <TableCell align="right">{t.estimated_hours ?? '—'}</TableCell>
                    <TableCell align="right">{t.actual_hours || 0}</TableCell>
                    <TableCell>
                      <Chip size="small" label={STATUS_LABELS[t.status] || t.status}
                        color={STATUS_COLORS[t.status] || 'default'} />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Editar, checklist y comentarios">
                        <IconButton size="small" onClick={() => setEditor({ taskId: t.id })}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Asignar">
                        <IconButton size="small" onClick={(e) => openAssign(e, t.id)}>
                          <PersonIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => removeTask(t.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {tasks.length === 0 && (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
            Sin tareas. Usa "Generar tareas" para volcar el paquete estandar de montaje y puesta
            en marcha, o "Nueva tarea" para crearlas una a una.
          </Typography>
        )}
      </Collapse>

      <Menu anchorEl={assignMenu?.anchor} open={!!assignMenu} onClose={() => setAssignMenu(null)}>
        {technicians.map((t) => (
          <MenuItem key={t.id} onClick={() => assignTo(t.id)}>
            {t.name}{t.in_team ? ' · en la OF' : ''}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => assignTo(null)}>Quitar asignacion</MenuItem>
      </Menu>

      <TaskEditorDialog
        open={!!editor}
        taskId={editor?.taskId || null}
        manufacturingOrderId={order.id}
        onClose={() => setEditor(null)}
        onSaved={async () => {
          await load();
          setExpanded(true);
          if (onChanged) onChanged();
        }}
      />
    </Box>
  );
}
